import React, { useCallback, useRef, memo, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { ReactComponent as EyePublicSvg } from '@assets/icons/eye-public.svg';
import { ReactComponent as EyePrivateSvg } from '@assets/icons/eye-private.svg';
import { useTypedSelector } from '@hooks';
import { toggleUploadAssetModal } from '@redux/actions/modalAction';
import { LgWidth, MdWidth } from '@components/Responsive';
import DropdownAssetVersions from '@components/MediaViewerHeader/components/DropdownAssetVersions';
import { useAuth } from '@hooks/useAuth';
import { closeEmbeddedMediaViewer } from '@helpers/uxpEmbeddedMediaViewer';

import './HeaderBreadcrumbs.less';

type HeaderBreadcrumbsProps = {
  isEmbeddedWebView?: boolean;
};

export default memo(function HeaderBreadcrumbs({
  isEmbeddedWebView = false
}: HeaderBreadcrumbsProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  const assets = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );

  const locationStateRef = useRef<{ returnTo: string } | undefined>(undefined);

  const { user } = useAuth();
  const isInternal = user?.type === 'internal';

  useEffect(() => {
    if (!state) return;
    locationStateRef.current = state;
  }, [state]);

  const goBack = useCallback(() => {
    if (isEmbeddedWebView) {
      if (closeEmbeddedMediaViewer()) return;
      navigate(-1);
      return;
    }

    if (!assets) return;

    const returnTo = locationStateRef.current?.returnTo;
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    const folderId = assets.path.slice(-1)[0]?.id;
    const { campaignId } = assets;
    if (folderId) {
      navigate(`/campaigns/single/${campaignId}/${folderId}`);
      return;
    }
    navigate(`/campaigns/single/${campaignId}`);
  }, [navigate, assets, isEmbeddedWebView]);

  const lastFolder = assets?.path?.[(assets?.path?.length || 1) - 1];

  if (isEmbeddedWebView && !selectedVersion) return null;

  const versionLabel = selectedVersion
    ? `${selectedVersion.name}.${selectedVersion.extension}`
    : '';

  return (
    <div className="media_viewer_breadcrumbs">
      <div className="media_viewer_breadcrumbs-wr">
        <Button
          className="media_viewer_btn b-only-icon tour-paused-media-viewer"
          onClick={goBack}
        >
          <ArrowLeftSvg />
        </Button>
        <div className="media_viewer_breadcrumbs-navigation">
          <LgWidth>
            {!!assets && (
              <div
                className="media_viewer_breadcrumbs-item"
                onClick={() =>
                  navigate(`/campaigns/single/${assets.campaignId}`)
                }
              >
                <Tooltip
                  title={assets.campaignName}
                  overlayClassName="toggle_favorite_overlay  toggle_favorite_overlay--small"
                  overlayInnerStyle={{ position: 'relative', top: '-30px' }}
                >
                  <span className="item-name">{assets.campaignName}</span>
                </Tooltip>
              </div>
            )}
            {assets?.path.map((folder) => (
              <div
                key={folder.id}
                className="media_viewer_breadcrumbs-item"
                onClick={() =>
                  navigate(
                    `/campaigns/single/${assets.campaignId}/${folder.id}`
                  )
                }
              >
                <Tooltip
                  title={folder.name}
                  overlayClassName="toggle_favorite_overlay  toggle_favorite_overlay--small"
                  overlayInnerStyle={{ position: 'relative', top: '-30px' }}
                >
                  <span className="item-name">{folder.name}</span>
                </Tooltip>
              </div>
            ))}
          </LgWidth>

          <div className="media_viewer_breadcrumbs-item">
            <Tooltip
              title={versionLabel}
              overlayClassName="toggle_favorite_overlay  toggle_favorite_overlay--small"
              overlayInnerStyle={{ position: 'relative', top: '-30px' }}
            >
              <span className="item-name">
                {versionLabel}
                <MdWidth>
                  {lastFolder && (
                    <span className="private-folder-indicator">
                      Private
                      {lastFolder?.isPrivate ? (
                        <EyePrivateSvg />
                      ) : (
                        <EyePublicSvg />
                      )}
                    </span>
                  )}
                </MdWidth>
              </span>
            </Tooltip>
          </div>
        </div>
        <DropdownAssetVersions
          external={!isInternal}
          isEmbeddedWebView={isEmbeddedWebView}
        />
        {!isEmbeddedWebView &&
          !!assets?.asset?.permissions.uploadNewVersion && (
            <Button
              className="media_viewer_btn b-only-icon new-version-btn tour-review-asset-upload"
              onClick={() => {
                dispatch(
                  toggleUploadAssetModal({
                    visible: true,
                    newVersionFromMediaViewer: true,
                    workspaceId: assets.workspaceId
                  })
                );
              }}
            >
              <PlusSvg className="b-white-icon" />
            </Button>
          )}
      </div>
    </div>
  );
});
